/**
 * <keystone-field-boolean> — checkbox.
 *
 * Maps onto JSON Schema `type: "boolean"`. Internal `value` stays
 * a string ("true" or "") to keep the field-widget contract consistent
 * across types. The <keystone-form> coerces to boolean at submit time.
 *
 * Layout differs from the other fields: checkbox sits on the same line
 * as the label.
 */

import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { fieldStyles, humaniseFieldName } from "./base.js";

@customElement("keystone-field-boolean")
export class KeystoneFieldBoolean extends LitElement {
  static override styles = fieldStyles;

  @property({ type: String }) name = "";
  @property({ type: String }) label = "";
  @property({ type: String }) value = "";
  @property({ type: Boolean }) required = false;
  @property({ type: String }) error: string | null = null;
  @property({ type: Boolean }) disabled = false;

  override updated(): void {
    if (this.error) this.setAttribute("data-error", "");
    else this.removeAttribute("data-error");
  }

  private onChange(e: Event): void {
    const input = e.target as HTMLInputElement;
    const next = input.checked ? "true" : "";
    this.value = next;
    this.dispatchEvent(
      new CustomEvent("keystone-field-input", {
        detail: { name: this.name, value: next },
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    const label = this.label || humaniseFieldName(this.name);
    const inputId = `input-${this.name}`;
    const checked = this.value === "true";

    return html`
      <div class="checkbox-row">
        <input
          id=${inputId}
          name=${this.name}
          type="checkbox"
          ?checked=${checked}
          ?required=${this.required}
          ?disabled=${this.disabled}
          aria-invalid=${this.error ? "true" : "false"}
          aria-describedby=${this.error ? `${inputId}-error` : ""}
          @change=${this.onChange}
        />
        <label for=${inputId}>
          ${label}${this.required
            ? html`<span class="required-marker" aria-hidden="true">*</span>`
            : ""}
        </label>
      </div>
      ${this.error
        ? html`<p class="error" id="${inputId}-error" role="alert">
            ${this.error}
          </p>`
        : ""}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "keystone-field-boolean": KeystoneFieldBoolean;
  }
}
